import React, { useEffect, useState, useRef } from 'react';
import Plot from 'react-plotly.js';
import PDBViewer from '../../components/PDBViewer';
import { verboseLog } from 'src/utils/config';

interface NetworkNode {
    id: string;
    label: string;
    group: string;
    color: string;
    shape?: string;
    icon?: string;
    gene?: any;
    drug?: any;
}

interface NetworkEdge {
    from: string;
    to: string;
    color?: string;
    width?: number;
    style?: string;
}

interface PlotlyNetworkVisualizationProps {
    nodes: NetworkNode[];
    edges: NetworkEdge[];
    onNodeClick: (gene: any) => void;
}

interface All3DStructuresPopupProps {
    nodes: NetworkNode[];
    onClose: () => void;
}

interface Inline3DViewerProps {
    pdbId: string;
    geneId: string;
}

const Inline3DViewer: React.FC<Inline3DViewerProps> = ({ pdbId, geneId }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [pdbUrl, setPdbUrl] = useState<string>('');
    const viewerRef = useRef<HTMLDivElement>(null);
    const viewer3DRef = useRef<any>(null);

    useEffect(() => {
        if (pdbId) {
            loadPDBData();
        }
    }, [pdbId]);

    const loadPDBData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Import the API function
            const { getPDBURL } = await import('../../services/api');
            
            // Get preassigned URL for PDB file from S3
            const response = await getPDBURL(pdbId);
            if (!response.success) {
                throw new Error(response.error || 'Failed to get PDB URL');
            }
            
            const { url } = response;
            verboseLog('🎯 Got PDB URL for inline viewer:', url);
            setPdbUrl(url);
            
            // Initialize 3Dmol viewer after URL is loaded
            setTimeout(() => {
                initialize3DmolViewer(url);
            }, 100);
            
        } catch (err) {
            console.error('Error loading PDB data for inline viewer:', err);
            setError(err instanceof Error ? err.message : 'Failed to load PDB data');
        } finally {
            setLoading(false);
        }
    };

    const initialize3DmolViewer = async (pdbUrl: string) => {
        if (!viewerRef.current) return;

        // Clear previous viewer
        viewerRef.current.innerHTML = '';

        if (!pdbUrl) {
            verboseLog('No PDB URL provided for inline viewer');
            return;
        }

        try {
            // Import 3Dmol dynamically
            const $3Dmol = await import('3dmol');
            
            const config = { backgroundColor: 'white' };
            const viewer = $3Dmol.createViewer(viewerRef.current, config);
            viewer3DRef.current = viewer;

            // Fetch the PDB file
            fetch(pdbUrl)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`Failed to fetch PDB file: ${response.status} ${response.statusText}`);
                    }
                    return response.text();
                })
                .then((data) => {
                    verboseLog('🎯 PDB content fetched for inline viewer, length:', data.length);
                    viewer.addModel(data, 'pdb');
                    viewer.setStyle({}, { cartoon: { color: 'spectrum' } });
                    viewer.zoomTo();
                    viewer.render();
                    verboseLog('🎯 Successfully loaded PDB file with inline 3Dmol');
                })
                .catch((error) => {
                    console.error('🎯 Error loading PDB file for inline viewer:', error);
                    setError('Failed to load 3D structure: ' + error.message);
                });

        } catch (err) {
            console.error('🎯 3Dmol viewer error for inline viewer:', err);
            setError('Failed to initialize 3D viewer: ' + (err instanceof Error ? err.message : String(err)));
        }
    };

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            {loading && (
                <div style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    color: '#666'
                }}>
                    <div>Loading 3D structure...</div>
                </div>
            )}
            
            {error && (
                <div style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    color: 'red',
                    padding: '20px'
                }}>
                    Error: {error}
                </div>
            )}
            
            {!loading && !error && pdbUrl && (
                <div 
                    ref={viewerRef}
                    style={{ 
                        width: '100%', 
                        height: '100%',
                        minHeight: '250px'
                    }}
                />
            )}
            
            {!loading && !error && !pdbUrl && (
                <div style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    color: '#666'
                }}>
                    No PDB data available
                </div>
            )}
        </div>
    );
};

const All3DStructuresPopup: React.FC<All3DStructuresPopupProps> = ({ nodes, onClose }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [geneStructures, setGeneStructures] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadAllGeneStructures();
    }, [nodes]);

    const loadAllGeneStructures = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Get all gene nodes with PDB IDs
            const geneNodes = nodes.filter(node => 
                node.group === 'gene' && 
                node.gene && 
                node.gene.pdb_ids && 
                node.gene.pdb_ids.length > 0
            );
            
            verboseLog('🎯 Found gene nodes with PDB data:', geneNodes);
            
            if (geneNodes.length === 0) {
                setError('No gene nodes with PDB structures found');
                setLoading(false);
                return;
            }
            
            // For now, just store the gene data - we'll implement the 3D rendering later
            setGeneStructures(geneNodes);
            setLoading(false);
            
        } catch (err) {
            console.error('Error loading gene structures:', err);
            setError('Failed to load gene structures');
            setLoading(false);
        }
    };

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 3000,
                padding: window.innerWidth <= 768 ? '10px' : '20px'
            }}
            onClick={(e) => {
                // Close popup when clicking on background
                if (e.target === e.currentTarget) {
                    verboseLog('🎯 Background clicked, closing popup');
                    onClose();
                }
            }}
        >
            <div 
                style={{
                    backgroundColor: 'white',
                    padding: window.innerWidth <= 768 ? '15px' : '20px',
                    borderRadius: '10px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    width: window.innerWidth <= 768 ? '95%' : '90%',
                    maxWidth: '1200px',
                    height: window.innerWidth <= 768 ? '95%' : '90%',
                    maxHeight: '800px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                }}
                onClick={(e) => {
                    // Prevent closing when clicking on popup content
                    e.stopPropagation();
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    borderBottom: '1px solid #eee',
                    paddingBottom: '15px',
                    flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                    gap: window.innerWidth <= 768 ? '10px' : '0'
                }}>
                    <h2 style={{ 
                        margin: 0, 
                        color: '#333',
                        fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.5rem',
                        textAlign: window.innerWidth <= 768 ? 'center' : 'left'
                    }}>
                        All Gene 3D Structures ({geneStructures.length} genes)
                    </h2>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            verboseLog('🎯 Close button clicked');
                            onClose();
                        }}
                        style={{
                            background: '#f5f5f5',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                            cursor: 'pointer',
                            color: '#333',
                            padding: window.innerWidth <= 768 ? '6px 10px' : '8px 12px',
                            minWidth: window.innerWidth <= 768 ? '36px' : '40px',
                            minHeight: window.innerWidth <= 768 ? '36px' : '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10000, // Ensure it's above other elements
                            // Mobile touch optimizations
                            touchAction: 'manipulation'
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflow: 'auto' }}>
                    {loading && (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <div>Loading 3D structures...</div>
                        </div>
                    )}
                    
                    {error && (
                        <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
                            Error: {error}
                        </div>
                    )}
                    
                    {!loading && !error && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
                            {geneStructures.map((geneNode, index) => (
                                <div key={geneNode.id} style={{
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '15px',
                                    backgroundColor: '#f9f9f9',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <h3 style={{ margin: '0 0 10px 0', color: '#333', textAlign: 'center' }}>
                                        {geneNode.label} ({geneNode.gene.ensembl_id})
                                    </h3>
                                    
                                    {/* 3D Structure Viewer */}
                                    <div style={{ 
                                        flex: 1,
                                        minHeight: '300px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        backgroundColor: 'white',
                                        position: 'relative'
                                    }}>
                                        <Inline3DViewer
                                            pdbId={geneNode.gene.pdb_ids[0]}
                                            geneId={geneNode.gene.ensembl_id}
                                        />
                                    </div>
                                    
                                    <div style={{ 
                                        marginTop: '10px', 
                                        padding: '8px', 
                                        backgroundColor: '#e3f2fd', 
                                        borderRadius: '4px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            PDB ID: {geneNode.gene.pdb_ids[0]}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const PlotlyNetworkVisualization: React.FC<PlotlyNetworkVisualizationProps> = ({ 
    nodes, 
    edges, 
    onNodeClick 
}) => {
    const [plotData, setPlotData] = useState<any[]>([]);
    const [plotLayout, setPlotLayout] = useState<any>({});
    const [showAllInfo, setShowAllInfo] = useState(false);
    const [pdbViewer, setPdbViewer] = useState<{isVisible: boolean, pdbId: string, geneId: string}>({
        isVisible: false,
        pdbId: '',
        geneId: ''
    });
    const [showAll3D, setShowAll3D] = useState<boolean>(false);
    const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
    const [mousePosition, setMousePosition] = useState<{x: number, y: number} | null>(null);
    

    useEffect(() => {
        if (!nodes || nodes.length === 0) return;

        verboseLog('PlotlyNetworkVisualization: nodes =', nodes);
        verboseLog('PlotlyNetworkVisualization: edges =', edges);

        // Group nodes by type
        const geneNodes = nodes.filter(n => n.group === 'gene');
        const drugNodes = nodes.filter(n => n.group === 'drug');
        const diseaseNodes = nodes.filter(n => n.group === 'disease');
        const targetNodes = nodes.filter(n => n.group === 'target');
        const imagingNodes = nodes.filter(n => n.group === 'imaging');

        // Create circular layout
        const allNodes = [...geneNodes, ...drugNodes, ...diseaseNodes, ...targetNodes, ...imagingNodes];
        const nEntities = allNodes.length;
        
        if (nEntities === 0) return;

        // Position entities in a circle
        const angles = Array.from({ length: nEntities }, (_, i) => 2 * Math.PI * i / nEntities);
        const radius = window.innerWidth > 768 ? 1.2 : 1.5; // Smaller radius for desktop, slightly larger for mobile
        const xPos = angles.map(angle => radius * Math.cos(angle));
        const yPos = angles.map(angle => radius * Math.sin(angle));

        // Create traces for each node type
        const traces: any[] = [];
        
        // Define colors and icons (matching the Python implementation)
        const colors = {
            'gene': '#FF6B6B',      // Red for genes
            'drug': '#4ECDC4',      // Teal for drugs  
            'disease': '#FF8C00',   // Orange for diseases
            'target': '#96CEB4',    // Green for targets
            'imaging': '#FFEAA7'    // Yellow for imaging
        };

        const icons = {
            'gene': '🧬',
            'drug': '💊', 
            'disease': '🦠',
            'target': '🎯',
            'imaging': '📷'
        };

        // Process each node type with responsive sizes
        const getResponsiveSize = (baseSize: number) => {
            if (window.innerWidth <= 480) {
                return Math.max(baseSize * 0.4, 20); // 40% of original size, minimum 20
            } else if (window.innerWidth <= 768) {
                return Math.max(baseSize * 0.6, 30); // 60% of original size, minimum 30
            }
            return baseSize; // Full size on desktop
        };

        const nodeTypes = [
            { type: 'gene', nodes: geneNodes, size: getResponsiveSize(100), symbol: 'circle' },
            { type: 'drug', nodes: drugNodes, size: getResponsiveSize(80), symbol: 'square' },
            { type: 'disease', nodes: diseaseNodes, size: getResponsiveSize(90), symbol: 'circle' },
            { type: 'target', nodes: targetNodes, size: getResponsiveSize(60), symbol: 'circle' },
            { type: 'imaging', nodes: imagingNodes, size: getResponsiveSize(60), symbol: 'diamond' }
        ];

        nodeTypes.forEach(({ type, nodes: typeNodes, size, symbol }) => {
            if (typeNodes.length === 0) return;

            const typeX = typeNodes.map(node => xPos[allNodes.indexOf(node)]);
            const typeY = typeNodes.map(node => yPos[allNodes.indexOf(node)]);

            // Create text with responsive icons
            const getResponsiveIconSize = () => {
                if (window.innerWidth <= 480) return 18; // Smaller icons on mobile
                if (window.innerWidth <= 768) return 24; // Medium icons on tablet
                return 36; // Full size icons on desktop
            };

            const getResponsiveTextSize = () => {
                if (window.innerWidth <= 480) return 8; // Smaller text on mobile
                if (window.innerWidth <= 768) return 10; // Medium text on tablet
                return 12; // Full size text on desktop
            };

            const entityText = typeNodes.map(node => {
                const iconSize = getResponsiveIconSize();
                const textSize = getResponsiveTextSize();
                const maxLabelLength = window.innerWidth <= 480 ? 8 : window.innerWidth <= 768 ? 10 : 12;
                const label = node.label.length > maxLabelLength ? node.label.substring(0, maxLabelLength) + '...' : node.label;
                return `<span style='font-size:${iconSize}px'>${icons[type as keyof typeof icons]}</span><br><span style='font-size:${textSize}px'>${label}</span>`;
            });


            traces.push({
                x: typeX,
                y: typeY,
                mode: 'markers+text',
                type: 'scatter',
                marker: {
                    size: size,
                    color: colors[type as keyof typeof colors],
                    line: { width: 3, color: 'white' },
                    symbol: symbol
                },
                text: entityText,
                textposition: "middle center",
                textfont: { 
                    size: window.innerWidth <= 480 ? 8 : window.innerWidth <= 768 ? 10 : 12, 
                    color: 'white', 
                    family: "Arial Black" 
                },
                name: `${icons[type as keyof typeof icons]} ${type.charAt(0).toUpperCase() + type.slice(1)}`,
                hoverinfo: 'skip',
                customdata: typeNodes.map(node => node.id) // Add customdata for click events
            });
        });

        // Add connection lines
        const connectionColors = {
            'solid': '#E74C3C',     // Red for gene-drug
            'dashed': '#3498DB',    // Blue for drug-disease  
            'dotted': '#F39C12',    // Orange for drug-target
            'dash-dot': '#9B59B6'   // Purple for disease-imaging
        };

        edges.forEach(edge => {
            const sourceNode = allNodes.find(n => n.id === edge.from);
            const targetNode = allNodes.find(n => n.id === edge.to);
            
            if (sourceNode && targetNode) {
                const sourceIdx = allNodes.indexOf(sourceNode);
                const targetIdx = allNodes.indexOf(targetNode);
                
                const sourceX = xPos[sourceIdx];
                const sourceY = yPos[sourceIdx];
                const targetX = xPos[targetIdx];
                const targetY = yPos[targetIdx];

                // Calculate direction vector and add offset
                const dx = targetX - sourceX;
                const dy = targetY - sourceY;
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                if (distance > 0) {
                    const dxNorm = dx / distance;
                    const dyNorm = dy / distance;
                    const offset = 0.2;
                    
                    const sourceXOffset = sourceX + dxNorm * offset;
                    const sourceYOffset = sourceY + dyNorm * offset;
                    const targetXOffset = targetX - dxNorm * offset;
                    const targetYOffset = targetY - dyNorm * offset;

                    // Determine line style and color
                    let lineStyle = 'solid';
                    let lineColor = '#E74C3C';
                    
                    if (edge.style === 'dashed') {
                        lineStyle = 'dash';
                        lineColor = '#3498DB';
                    } else if (edge.style === 'dotted') {
                        lineStyle = 'dot';
                        lineColor = '#F39C12';
                    } else if (edge.style === 'dash-dot') {
                        lineStyle = 'dashdot';
                        lineColor = '#9B59B6';
                    }

                    traces.push({
                        x: [sourceXOffset, targetXOffset],
                        y: [sourceYOffset, targetYOffset],
                        mode: 'lines',
                        type: 'scatter',
                        line: {
                            color: lineColor,
                            width: 3,
                            dash: lineStyle
                        },
                        showlegend: false,
                        hoverinfo: 'skip'
                    });
                }
            }
        });

        // Create annotations for showAllInfo
        const annotations: any[] = [];
        if (showAllInfo) {
            // Group nodes by type for annotation generation
            const nodesByType = allNodes.reduce((acc, node) => {
                const type = node.group;
                if (!acc[type]) acc[type] = [];
                acc[type].push(node);
                return acc;
            }, {} as Record<string, typeof allNodes>);

            // Generate annotations for each node type
            Object.entries(nodesByType).forEach(([type, typeNodes]) => {
                const icon = icons[type as keyof typeof icons];
                const typeName = type.charAt(0).toUpperCase() + type.slice(1);
                
                typeNodes.forEach((node, nodeIndex) => {
                    const nodeIdx = allNodes.indexOf(node);
                    const x = xPos[nodeIdx];
                    const y = yPos[nodeIdx];
                    
                    // Generate info text based on node type
                    let infoText = '';
                    if (type === 'gene' && node.gene) {
                        infoText = `<b>${icon} ${typeName}</b><br>ID: ${node.id.replace('gene_', '')}<br>Symbol: ${node.label}<br>Name: ${node.gene.gene_name || 'Unknown'}<br>Function: ${node.gene.gene_function || 'Unknown'}`;
                    } else if (type === 'drug') {
                        const drugName = node.label;
                        const company = node.drug?.company_name || 'Unknown Company';
                        const intendedUse = node.drug?.intended_use || node.drug?.indication || 'FDA Approved Drug';
                        
                        // Wrap long text for intended use
                        const wrapText = (text: string, maxLength: number = 40): string => {
                            if (text.length <= maxLength) return text;
                            const words = text.split(' ');
                            const lines: string[] = [];
                            let currentLine = '';
                            for (const word of words) {
                                if ((currentLine + word).length <= maxLength) {
                                    currentLine += (currentLine ? ' ' : '') + word;
                                } else {
                                    if (currentLine) lines.push(currentLine);
                                    currentLine = word;
                                }
                            }
                            if (currentLine) lines.push(currentLine);
                            return lines.join('<br>');
                        };
                        
                        const wrappedIntendedUse = wrapText(intendedUse, 35);
                        infoText = `<b>${icon} ${typeName}</b><br>Name: ${drugName}<br>Company: ${company}<br>Use: ${wrappedIntendedUse}`;
                    } else if (type === 'disease') {
                        infoText = `<b>${icon} ${typeName}</b><br>Name: ${node.label}<br>Type: Medical Condition<br>Diagnosis: Clinical Assessment`;
                    } else if (type === 'target') {
                        infoText = `<b>${icon} ${typeName}</b><br>Protein: ${node.label}<br>Type: Therapeutic Target<br>Function: Drug Binding Site`;
                    } else if (type === 'imaging') {
                        infoText = `<b>${icon} ${typeName}</b><br>Modality: ${node.label}<br>Type: Medical Imaging<br>Purpose: Diagnostic Tool`;
                    } else {
                        infoText = `<b>${icon} ${typeName}</b><br>Name: ${node.label}`;
                    }
                    
                    // Calculate responsive width based on window size (2/3 of previous width)
                    const windowWidth = window.innerWidth;
                    const responsiveWidth = Math.min(Math.max(windowWidth * 0.167, 133), 267); // 2/3 of 25% = ~16.7%, min 133px, max 267px
                    
                    const annotation = {
                        x: x,
                        y: y,
                        xref: 'x',
                        yref: 'y',
                        text: infoText,
                        showarrow: true,
                        arrowhead: 2,
                        arrowsize: 1,
                        arrowwidth: 2,
                        arrowcolor: '#666666',
                        ax: 20,
                        ay: -20,
                        bgcolor: 'rgba(255,255,255,0.7)',
                        bordercolor: '#666666',
                        borderwidth: 1,
                        borderpad: 6,
                        font: { size: 11, color: '#333333', family: 'Arial' },
                        width: responsiveWidth,
                        height: 'auto',
                        align: 'left'
                    };
                    annotations.push(annotation);
                });
            });
        }

        // Set layout
        const layout = {
            title: {
                text: 'Comprehensive Biomedical Network Analysis',
                font: { 
                    size: window.innerWidth <= 480 ? 10 : window.innerWidth <= 768 ? 12 : 20, 
                    color: '#333' 
                },
                // Move title slightly lower on mobile, down on desktop to avoid toolbar overlap
                y: window.innerWidth <= 480 ? 0.90 : window.innerWidth <= 768 ? 0.88 : 0.90
            },
            xaxis: {
                showgrid: false,
                zeroline: false,
                showticklabels: false,
                range: window.innerWidth > 768 ? [-2, 2] : [-2.5, 2.5] // Smaller range for desktop
            },
            yaxis: {
                showgrid: false,
                zeroline: false,
                showticklabels: false,
                range: window.innerWidth > 768 ? [-2, 2] : [-2.5, 2.5] // Smaller range for desktop
            },
            plot_bgcolor: 'white',
            paper_bgcolor: 'white',
            showlegend: true,
            legend: {
                x: 1.02,
                y: 1, // Normal legend position since buttons are at bottom on mobile
                bgcolor: 'rgba(255,255,255,0.8)',
                bordercolor: '#ccc',
                borderwidth: 1,
                // Remove color indicators, show only icons
                traceorder: 'normal',
                // Hide color boxes in legend by setting itemwidth to 0
                itemwidth: 0,
                itemsizing: 'constant'
            },
            // Adjust margins based on screen size and button position
            margin: { 
                l: window.innerWidth <= 480 ? 30 : window.innerWidth <= 768 ? 40 : 80, // More left margin for desktop
                r: window.innerWidth <= 480 ? 30 : window.innerWidth <= 768 ? 40 : 80, // More right margin for desktop
                t: window.innerWidth <= 480 ? 80 : window.innerWidth <= 768 ? 90 : 100, // Increased top margin for title clearance
                b: window.innerWidth <= 480 ? 100 : window.innerWidth <= 768 ? 110 : 80 // Increased bottom margin for desktop to prevent node cutoff
            },
            annotations: annotations
        };

        verboseLog('🎯 Plot traces created:', traces);
        verboseLog('🎯 Nodes data:', nodes);
        setPlotData(traces);
        setPlotLayout(layout);
        
    }, [nodes, edges, showAllInfo]);


    const handleShowAllInfo = () => {
        setShowAllInfo(!showAllInfo);
    };

    // Add keyboard event listener for 'H' key
    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'h' || event.key === 'H') {
                setShowAllInfo(!showAllInfo);
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [showAllInfo]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
            }
        };
    }, [hoverTimeout]);

    // Debug PDB viewer state changes
    useEffect(() => {
        verboseLog('🎯 PDB Viewer State Changed:', pdbViewer);
    }, [pdbViewer]);

    const handlePlotClick = (event: any) => {
        verboseLog('🎯 PLOTLY CLICK EVENT:', event);
        verboseLog('🎯 Event points:', event.points);
        
        if (event.points && event.points.length > 0) {
            const point = event.points[0];
            verboseLog('🎯 Point data:', point);
            verboseLog('🎯 Point customdata:', point.customdata);
            
            const nodeId = point.customdata;
            verboseLog('🎯 Clicked node ID:', nodeId);
            
            // Find the corresponding node
            const node = nodes.find(n => n.id === nodeId);
            verboseLog('🎯 Found node:', node);
            
            if (node && node.group === 'gene' && node.gene && node.gene.pdb_ids && node.gene.pdb_ids.length > 0) {
                verboseLog('🎯 Gene node clicked, opening PDB viewer for:', node.gene.ensembl_id);
                setPdbViewer({
                    isVisible: true,
                    pdbId: node.gene.pdb_ids[0],
                    geneId: node.gene.ensembl_id
                });
            } else {
                verboseLog('🎯 Not a gene node or no PDB data available');
            }
        } else {
            verboseLog('🎯 No points in click event - click was not on a data point');
        }
    };




    return (
        <div 
            style={{ 
                position: 'relative', 
                width: '100%', 
                height: window.innerWidth <= 480 ? '525px' : window.innerWidth <= 768 ? '675px' : '600px', // 1:1.5 ratio only for mobile
                // Mobile optimizations
                minHeight: window.innerWidth <= 480 ? '450px' : window.innerWidth <= 768 ? '600px' : '600px',
                maxHeight: window.innerWidth <= 768 ? '700px' : 'none',
                overflow: 'hidden'
            }}
        >
            <Plot
                data={plotData}
                layout={plotLayout}
                style={{ 
                    width: '100%', 
                    height: '100%',
                    // Mobile touch optimizations
                    touchAction: 'manipulation',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none'
                }}
                onClick={handlePlotClick}
                config={{
                    displayModeBar: true,
                    displaylogo: false,
                    modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
                    doubleClick: false,
                    scrollZoom: false,
                    editable: false,
                    staticPlot: false,
                    // Mobile optimizations
                    responsive: true,
                    showTips: true,
                    showLink: false,
                    linkText: '',
                    toImageButtonOptions: {
                        format: 'png',
                        filename: 'network',
                        height: 600,
                        width: 800,
                        scale: 1
                    }
                }}
            />
            
            {/* Show All Info Button - moved to top left to avoid legend overlap */}
            {/* Mobile Button Container */}
            <div style={{
                position: 'absolute',
                top: window.innerWidth <= 768 ? 'auto' : '10px',
                bottom: window.innerWidth <= 768 ? '10px' : 'auto',
                left: '10px',
                right: '10px',
                zIndex: 1000,
                display: 'flex',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                gap: window.innerWidth <= 768 ? '8px' : '10px',
                flexWrap: 'wrap'
            }}>
                {/* Show All Info Button - mobile optimized */}
                <button
                    onClick={handleShowAllInfo}
                    style={{
                        padding: window.innerWidth <= 768 ? '8px 12px' : '8px 16px',
                        backgroundColor: showAllInfo ? '#f44336' : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: window.innerWidth <= 768 ? '12px' : '12px',
                        fontWeight: 'bold',
                        // Mobile touch optimizations
                        touchAction: 'manipulation',
                        minHeight: '36px',
                        minWidth: window.innerWidth <= 768 ? '120px' : '100px',
                        flex: window.innerWidth <= 768 ? '1' : 'none'
                    }}
                >
                    {showAllInfo ? 'Hide All Info' : 'Show All Info'}
                </button>

                {/* Show 3D Structure Button */}
                <button
                    onClick={() => setShowAll3D(true)}
                    style={{
                        padding: window.innerWidth <= 768 ? '8px 12px' : '8px 16px',
                        backgroundColor: '#9C27B0',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: window.innerWidth <= 768 ? '12px' : '12px',
                        fontWeight: 'bold',
                        // Mobile touch optimizations
                        touchAction: 'manipulation',
                        minHeight: '36px',
                        minWidth: window.innerWidth <= 768 ? '120px' : '100px',
                        flex: window.innerWidth <= 768 ? '1' : 'none'
                    }}
                >
                    Show 3D Structure
                </button>
            </div>

            {/* Instructions - mobile optimized */}
            <div style={{
                position: 'absolute',
                bottom: window.innerWidth <= 768 ? '140px' : '10px', // Move up on mobile to avoid button overlap
                left: '10px',
                right: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                padding: window.innerWidth <= 768 ? '8px' : '8px',
                borderRadius: '8px',
                fontSize: window.innerWidth <= 768 ? '11px' : '11px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                maxWidth: 'calc(100% - 20px)',
                // Mobile: hide on mobile since buttons are at bottom
                display: window.innerWidth <= 768 ? 'none' : 'block'
            }}>
                <div><strong>Shortcuts:</strong></div>
                <div>• Press 'H' to toggle detailed info</div>
                <div>• Click 'Show All Info' button</div>
                <div>• {window.innerWidth <= 768 ? 'Tap' : 'Hover over'} nodes for quick info</div>
            </div>
            
            {/* PDB 3D Structure Viewer */}
            <PDBViewer
                pdbId={pdbViewer.pdbId}
                geneId={pdbViewer.geneId}
                isVisible={pdbViewer.isVisible}
                onClose={() => setPdbViewer({ isVisible: false, pdbId: '', geneId: '' })}
            />

            {/* All 3D Structures Popup */}
            {showAll3D && (
                <All3DStructuresPopup
                    nodes={nodes}
                    onClose={() => setShowAll3D(false)}
                />
            )}
        </div>
    );
};

export default PlotlyNetworkVisualization;
